import { Params } from '@angular/router';

/**
 * Coordinación de la sesión entre pestañas del mismo navegador.
 *
 * Un operador suele tener varias pestañas abiertas. Cuando la sesión vence
 * —el tope de 12 h, típicamente con el portátil cerrado—, cada pestaña cae en
 * el login con su página anotada en `?volver=`. Al volver a entrar en una sola
 * de ellas, las demás se enteran por el evento `storage` y regresan cada una
 * a su página. No se abren pestañas nuevas: el navegador bloquea abrir más de
 * una por clic, y además no hace falta, porque las pestañas nunca se cerraron.
 */

/** Parámetro del login con la página a la que volver. */
export const RETURN_PARAM = 'volver';

/**
 * Identificador del último login exitoso en este navegador.
 *
 * Cumple dos funciones. Escribirlo es el aviso a las demás pestañas de que hay
 * sesión nueva. Y compararlo con el que conoce cada pestaña impide que una
 * pestaña atrasada —con la sesión vieja todavía en memoria— cierre la sesión
 * recién abierta en otra: `signOut()` termina en `localStorage.clear()`, que
 * es compartido.
 *
 * `signOut()` lo borra junto con todo lo demás, y está bien: sin id no hay
 * login nuevo que proteger.
 */
export const LOGIN_ID_KEY = 'aguacate_login_id';

/**
 * `true` si la URL es una página del dashboard a la que se puede volver.
 *
 * Sólo rutas internas bajo `/wallet`: nada de `//host`, esquemas ni barras
 * invertidas, que algunos navegadores normalizan a `/`. `navigateByUrl` no
 * sale del sitio, pero la recarga de las otras pestañas usa
 * `location.assign`, que sí.
 */
export function isSafeReturnUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.length > 2000) return false;
  if (url.includes('\\') || url.startsWith('//')) return false;
  return url === '/wallet' || url.startsWith('/wallet/') || url.startsWith('/wallet?');
}

/** Query params del login para volver a `url`, o nada si no corresponde. */
export function loginReturnParams(url: string | null | undefined): Params | undefined {
  return isSafeReturnUrl(url) ? { [RETURN_PARAM]: url } : undefined;
}

/** Ruta completa del login con la vuelta a `url`, para `location.assign`. */
export function loginHrefWithReturn(url: string | null | undefined): string {
  return isSafeReturnUrl(url)
    ? `/auth/login?${RETURN_PARAM}=${encodeURIComponent(url)}`
    : '/auth/login';
}

/** Página anotada en la URL del login, si es válida. */
export function readReturnUrl(search: string = window.location.search): string | null {
  try {
    const value = new URLSearchParams(search).get(RETURN_PARAM);
    return isSafeReturnUrl(value) ? value : null;
  } catch {
    return null;
  }
}

export function readLoginId(): string | null {
  try {
    return localStorage.getItem(LOGIN_ID_KEY);
  } catch {
    return null; // Safari en modo privado
  }
}

/** Anota un login nuevo y lo devuelve. Dispara `storage` en las demás pestañas. */
export function writeNewLoginId(): string | null {
  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem(LOGIN_ID_KEY, id);
    return id;
  } catch {
    return null; // Safari en modo privado: sin coordinación entre pestañas
  }
}
